import {
  formatMinor,
  PERIOD_STATUS_LABELS,
  type FinancialPeriod,
  type ReadinessItem,
} from '@hvac/contracts';
import { Alert, Button, Card, CardBody, CardHeader, Pill, Skeleton } from '@hvac/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  closeKeys,
  closePeriod,
  ensurePeriods,
  periodsQuery,
  readinessQuery,
} from '../../shared/api/close-queries.js';
import { sessionQuery } from '../../shared/api/queries.js';
import { ReturnPanel } from './return-panel.js';
import { SignOffPanel } from './sign-off-panel.js';

/**
 * The Close.
 *
 * The screen an accountant actually lives in at month end, and the one this
 * product has never had. Two ideas carry the layout.
 *
 * The months are a spine down the left, newest first, each carrying its own
 * state. Picking one is the only navigation on the page, because closing a
 * month is a single task and a task should not be spread across a menu.
 *
 * The checklist is the hero. It is not a summary of the month, it is the list
 * of things standing between this month and a signature, written by the books
 * about themselves, each row linking to the exact work that clears it.
 */

/** Where each kind of blocker is actually resolved. */
const DESTINATION: Readonly<Record<string, { to: string; label: string }>> = {
  documents_in_review: { to: '/review', label: 'Open the review queue' },
  records_in_draft: { to: '/bookkeeping', label: 'Open the records' },
  // records_ready_to_sign has no destination: it is signed right here.
  records_without_supplier: { to: '/bookkeeping', label: 'Open the records' },
  lines_uncategorised: { to: '/bookkeeping', label: 'Open the records' },
  capital_without_class: { to: '/bookkeeping', label: 'Open the records' },
  receipts_missing: { to: '/bank', label: 'See what is missing' },
  deposits_unexplained: { to: '/bank', label: 'Explain the deposits' },
  unverified_rules: { to: '/tax', label: 'Open the rule sets' },
};

export function ClosePage() {
  const { data: session } = useSuspenseQuery(sessionQuery());
  const organizationId = session.activeOrganizationId ?? '';
  const queryClient = useQueryClient();

  const periods = useQuery(periodsQuery(organizationId));
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const list = periods.data ?? [];
  const selected = list.find((period) => period.id === selectedId) ?? list[0] ?? null;

  const readiness = useQuery({
    ...readinessQuery(organizationId, selected?.id ?? ''),
    enabled: selected !== null,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: closeKeys.all(organizationId) });
  };

  const close = useMutation({
    mutationFn: (status: 'open' | 'soft_closed' | 'closed') =>
      closePeriod(organizationId, selected?.id ?? '', { status }),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    // The database refuses a close it cannot honour and names the reason.
    // Shown exactly as written rather than replaced with "failed".
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'That period could not be closed.'),
  });

  const generate = useMutation({
    mutationFn: () => ensurePeriods(organizationId, new Date().getFullYear()),
    onSuccess: invalidate,
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'Those periods could not be created.'),
  });

  if (periods.isPending) {
    return (
      <div className="flex flex-col gap-3" aria-label="Loading">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const items = readiness.data ?? [];
  const blocking = items.filter((item) => item.severity === 'blocking');
  const advisory = items.filter((item) => item.severity === 'advisory');
  const blockingCount = blocking.reduce((total, item) => total + item.itemCount, 0);

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-xl font-semibold text-content">Close</h1>
        <p className="mt-1 text-sm text-content-muted">
          What stands between this month and a set of books somebody can sign.
        </p>
      </div>

      {error === null ? null : (
        <Alert tone="danger" title="That did not happen">
          {error}
        </Alert>
      )}

      {list.length === 0 ? (
        <Card>
          <CardBody className="flex flex-col items-start gap-3 py-10">
            <p className="text-sm text-content-muted">
              This company has no periods yet, so there is nothing to close. Creating this year
              takes a moment and can be done again safely.
            </p>
            <Button onClick={() => generate.mutate()} isLoading={generate.isPending}>
              Create this year
            </Button>
          </CardBody>
        </Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[16rem_1fr]">
          <PeriodSpine
            periods={list}
            selectedId={selected?.id ?? null}
            onSelect={(id) => {
              setError(null);
              setSelectedId(id);
            }}
          />

          <div className="flex flex-col gap-4">
            {selected === null ? null : (
              <Card>
                <CardHeader
                  title={selected.label}
                  description={
                    selected.status === 'closed'
                      ? `Closed${selected.closedByName === null ? '' : ` by ${selected.closedByName}`}. Nothing dated inside it can change.`
                      : readiness.isPending
                        ? 'Working out what is left.'
                        : blockingCount === 0
                          ? 'Nothing is outstanding. This month is ready to close.'
                          : `${String(blockingCount)} thing${blockingCount === 1 ? '' : 's'} still need a person.`
                  }
                />
                <CardBody className="flex flex-col gap-3">
                  {readiness.isPending ? (
                    <Skeleton className="h-24 w-full" />
                  ) : items.length === 0 ? (
                    <p className="animate-rise rounded-[var(--radius-control)] bg-brand-50 px-4 py-6 text-sm text-brand-700">
                      Every document is reviewed, every record is posted, every line has an account,
                      and the bank agrees. This is what a finished month looks like.
                    </p>
                  ) : (
                    <>
                      {blocking.map((item) =>
                        item.kind === 'records_ready_to_sign' ? (
                          <div key={item.kind} className="flex flex-col gap-2">
                            <ReadinessRow item={item} />
                            <SignOffPanel organizationId={organizationId} periodId={selected.id} />
                          </div>
                        ) : (
                          <ReadinessRow key={item.kind} item={item} />
                        ),
                      )}
                      {advisory.map((item) => (
                        <ReadinessRow key={item.kind} item={item} />
                      ))}
                    </>
                  )}

                  <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-border pt-4">
                    {selected.status === 'closed' ? (
                      <Button
                        variant="secondary"
                        onClick={() => close.mutate('open')}
                        isLoading={close.isPending}
                      >
                        Reopen this month
                      </Button>
                    ) : (
                      <>
                        <Button
                          onClick={() => close.mutate('closed')}
                          isLoading={close.isPending}
                          disabled={blockingCount > 0}
                        >
                          Close {selected.label}
                        </Button>
                        {selected.status === 'open' ? (
                          <Button
                            variant="secondary"
                            onClick={() => close.mutate('soft_closed')}
                            isLoading={close.isPending}
                          >
                            Start closing
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            onClick={() => close.mutate('open')}
                            isLoading={close.isPending}
                          >
                            Reopen to everyone
                          </Button>
                        )}
                        {blockingCount > 0 ? (
                          <span className="text-xs text-content-muted">
                            Starting to close warns the team while you finish the list.
                          </span>
                        ) : null}
                      </>
                    )}
                  </div>
                </CardBody>
              </Card>
            )}

            <ReturnPanel organizationId={organizationId} />
          </div>
        </div>
      )}
    </div>
  );
}

function PeriodSpine({
  periods,
  selectedId,
  onSelect,
}: {
  periods: FinancialPeriod[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <Card className="h-fit">
      <CardBody className="flex flex-col gap-0.5 p-2">
        {periods.map((period) => {
          const isSelected = period.id === selectedId;
          return (
            <button
              key={period.id}
              type="button"
              onClick={() => onSelect(period.id)}
              aria-current={isSelected}
              className={[
                'flex items-center justify-between gap-2 rounded-[var(--radius-control)] px-3 py-2 text-left text-sm',
                'transition-[background-color,color] duration-[var(--duration-quick)]',
                isSelected
                  ? 'bg-brand-50 font-medium text-brand-700'
                  : 'text-content-muted hover:bg-surface-sunken hover:text-content',
              ].join(' ')}
            >
              <span>{period.label}</span>
              {period.status === 'open' ? null : (
                <Pill tone={period.status === 'closed' ? 'neutral' : 'warning'}>
                  {PERIOD_STATUS_LABELS[period.status]}
                </Pill>
              )}
            </button>
          );
        })}
      </CardBody>
    </Card>
  );
}

function ReadinessRow({ item }: { item: ReadinessItem }) {
  const destination = DESTINATION[item.kind];
  const isBlocking = item.severity === 'blocking';

  return (
    <div className="animate-rise flex items-start gap-3 rounded-[var(--radius-control)] border border-border px-4 py-3">
      <span
        className={[
          'mt-0.5 min-w-8 rounded-full px-2 py-0.5 text-center text-sm font-medium tabular',
          isBlocking ? 'bg-danger/10 text-danger' : 'bg-warning/10 text-warning',
        ].join(' ')}
      >
        {item.itemCount}
      </span>
      <div className="flex-1">
        <p className="text-sm font-medium text-content">{item.headline}</p>
        <p className="mt-0.5 text-sm text-content-muted">{item.detail}</p>
      </div>
      {destination === undefined ? null : (
        <Link
          to={destination.to}
          className="shrink-0 self-center text-sm text-brand-600 hover:text-brand-700"
        >
          {destination.label}
        </Link>
      )}
    </div>
  );
}

/** Exported for the return panel, which shows the same money the same way. */
export { formatMinor };
